import React from 'react';
import { ChevronLeft } from 'lucide-react';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 flex justify-center py-8">
            <div className="w-full max-w-3xl px-4 lg:px-8">
                <button
                    onClick={() => window.location.hash = '/'}
                    className="inline-flex items-center text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-200 font-medium mb-8 transition-colors"
                >
                    <ChevronLeft size={18} className="mr-1" />
                    Back to Home
                </button>

                <article className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl p-8 md:p-12 border border-gray-200 dark:border-slate-800">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Privacy Policy</h1>

                    <div className="prose dark:prose-invert max-w-none text-gray-600 dark:text-slate-300 space-y-4">
                        <p>Last updated: {new Date().toLocaleDateString()}</p>

                        <p>
                            At <strong>BrieflyCA</strong>, we respect your privacy and are committed to protecting your personal information.
                            This Privacy Policy describes how we collect, use, store, and safeguard data when
                            you visit our website or use the BrieflyCA mobile application.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            1. Information We Collect
                        </h2>

                        <p>
                            BrieflyCA does <strong>not</strong> require account creation and does <strong>not</strong>
                            collect personally identifiable information unless you contact us directly.
                        </p>

                        <h3 className="text-lg font-semibold mt-4">a) Information You Provide Voluntarily</h3>
                        <p>
                            If you reach out to us via email or form submissions, we may collect your name,
                            email address, and the message content.
                        </p>

                        <h3 className="text-lg font-semibold mt-4">b) Automatically Collected Data</h3>
                        <p>We use privacy-focused analytics tools (such as Firebase) to collect:</p>

                        <ul className="list-disc pl-5 space-y-1">
                            <li>Crash logs and performance data</li>
                            <li>Device information (model, OS version, app version)</li>
                            <li>Anonymous usage metrics such as screen visits and interactions</li>
                        </ul>

                        <h3 className="text-lg font-semibold mt-4">c) Local Device Storage</h3>
                        <p>We store the following <strong>only on your device</strong>:</p>

                        <ul className="list-disc pl-5 space-y-1">
                            <li>Theme preference (Light/Dark)</li>
                            <li>Notification settings</li>
                            <li>Category preferences</li>
                        </ul>

                        <p>This information never leaves your device.</p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            2. How We Use Your Information
                        </h2>

                        <ul className="list-disc pl-5 space-y-1">
                            <li>To provide fresh Canadian news and improve your browsing experience</li>
                            <li>To ensure app performance, stability, and security</li>
                            <li>To send optional push notifications</li>
                            <li>To analyze and improve overall app and website performance</li>
                        </ul>

                        <p>
                            We <strong>do not</strong> sell your data, share personal information with advertisers,
                            or track you across apps or websites.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            3. Cookies & Tracking Technologies
                        </h2>

                        <p>
                            Our website may use cookies to enhance functionality and analyze traffic.
                            These cookies do not identify you personally and can be disabled in your browser settings.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            4. Third-Party Services
                        </h2>

                        <p>We use the following trusted services:</p>

                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Firebase Analytics & Crashlytics</strong> – for anonymized analytics and crash reporting.</li>
                            <li><strong>Firebase Cloud Messaging</strong> – to deliver category-based notifications.</li>
                            <li><strong>Firestore</strong> – to display non-personalized ads such as AutoLog promotions.</li>
                        </ul>

                        <p>
                            These services may collect anonymized technical data as part of their operation,
                            but no personally identifying information is shared.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            5. Advertising
                        </h2>

                        <p>
                            BrieflyCA may display non-intrusive banner ads. Ads are not personalized,
                            and we do not share personal data with advertisers.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            6. Data Security
                        </h2>

                        <p>
                            We use industry-standard security measures to protect all app and website data.
                            No personally identifiable information is stored on our servers.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            7. Children’s Privacy
                        </h2>

                        <p>
                            BrieflyCA is not intended for children under 13.
                            We do not knowingly collect data from children.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            8. Your Choices & Rights
                        </h2>

                        <ul className="list-disc pl-5 space-y-1">
                            <li>You may disable notifications at any time.</li>
                            <li>You may delete all local data by uninstalling the app.</li>
                            <li>You may request removal of any voluntarily provided information.</li>
                        </ul>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            9. Updates to This Policy
                        </h2>

                        <p>
                            We may update this Privacy Policy from time to time.
                            Changes will be posted on this page and reflected in the app.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            10. Contact Us
                        </h2>

                        <p>
                            If you have questions or concerns about this Privacy Policy, contact us at:
                        </p>

                        <p>
                            📧 <strong>info@askstudios.ca</strong><br />
                            🌐 <strong>https://brieflyca.vercel.app</strong>
                        </p>
                    </div>
                </article>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
